import Swal from "sweetalert2";

/* ----------------------------------------------------
   1. Hardcoded fallback for chain restaurants
---------------------------------------------------- */

const TIMEZONE_MAP = {
  EST: 'America/New_York',
  CST: 'America/Chicago',
  MST: 'America/Denver',
  PST: 'America/Los_Angeles',
};

const CHAIN_FALLBACK_HOURS = {
  7: [
    { day: 0, start: "11:00", end: "20:45" },
    { day: 1, start: "11:00", end: "20:45" },
    { day: 3, start: "11:00", end: "20:45" },
    { day: 4, start: "11:00", end: "20:45" },
    { day: 5, start: "11:00", end: "20:45" },
    { day: 6, start: "11:00", end: "20:45" },
  ],
  11: [
    { day: 1, start: "11:00", end: "14:25" },
    { day: 1, start: "16:30", end: "21:15" },
    { day: 2, start: "11:00", end: "14:25" },
    { day: 2, start: "16:30", end: "21:15" },
    { day: 3, start: "11:00", end: "14:25" },
    { day: 3, start: "16:30", end: "21:15" },
    { day: 4, start: "11:00", end: "14:25" },
    { day: 4, start: "16:30", end: "21:15" },
    { day: 5, start: "11:00", end: "21:45" },
    { day: 6, start: "10:00", end: "13:00" },
  ],
  40: [
    { day: 1, start: "10:00", end: "19:45" },
    { day: 2, start: "10:00", end: "19:45" },
    { day: 3, start: "10:00", end: "19:45" },
    { day: 4, start: "10:00", end: "19:45" },
    { day: 5, start: "10:00", end: "19:45" },
    { day: 6, start: "10:00", end: "19:45" },
  ],
  73: [
    { day: 1, start: "10:30", end: "20:15" },
    { day: 2, start: "10:30", end: "20:15" },
    { day: 3, start: "10:30", end: "20:15" },
    { day: 4, start: "10:30", end: "20:15" },
    { day: 5, start: "10:30", end: "20:15" },
    { day: 6, start: "10:30", end: "20:15" },
  ],
};

/* ----------------------------------------------------
   2. API-Based Unified Opening Logic
---------------------------------------------------- */
export const isStoreOpen = (id, hoursFromAPI) => {
  // If API hours exist → use API system
  if (hoursFromAPI && hoursFromAPI.time_zone) {
    return isRestaurantOpenAPI(hoursFromAPI);
  }

  // Otherwise fallback for chain restaurants
  return isStoreOpenFallback(id);
};

/* ----------------------------------------------------
   3. API logic
---------------------------------------------------- */
const isRestaurantOpenAPI = (hours) => {
  const now = new Date();

  const timeZone = TIMEZONE_MAP[hours?.time_zone] || "America/New_York";

  const currentDay = now
    .toLocaleString("en-US", { timeZone, weekday: "long" })
    .toLowerCase();

  const currentDate = new Date().toLocaleDateString("en-CA", { timeZone });

  // Check special hours
  const specialToday = hours?.specialhours?.find((d) => d.date === currentDate);

  const currentTimeStr = now.toLocaleTimeString("en-US", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });

  const getMinutes = (str) => {
    const [h, m] = str.split(":").map(Number);
    return h * 60 + m;
  };

  const nowMinutes = getMinutes(currentTimeStr);

  // SPECIAL HOURS override
  if (specialToday) {
    if (specialToday.is_closed) return false;

    if (specialToday.open_time && specialToday.close_time) {
      const open = getMinutes(specialToday.open_time);
      const close = getMinutes(specialToday.close_time);
      return nowMinutes >= open && nowMinutes <= close;
    }
  }

  // Regular weekly hours  
  const todayRaw = hours[currentDay];
  if (!todayRaw || todayRaw.toLowerCase() === "closed") return false;

  const [openClose, breakStr] = todayRaw.split("|");
  const [openStr, closeStr] = openClose.split("-");

  const openMinutes = getMinutes(openStr);
  const closeMinutes = getMinutes(closeStr);

  if (nowMinutes < openMinutes || nowMinutes > closeMinutes) return false;

  if (breakStr) {
    const [breakStart, breakEnd] = breakStr.split("-");
    const breakStartMin = getMinutes(breakStart);
    const breakEndMin = getMinutes(breakEnd);

    if (nowMinutes >= breakStartMin && nowMinutes < breakEndMin) return false;
  }

  return true;
};

/* ----------------------------------------------------
   4. Fallback to hardcoded chain-hours
---------------------------------------------------- */
const isStoreOpenFallback = (id) => {
  const now = new Date();
  const centralNow = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));

  const currentDay = centralNow.getDay();
  const currentTime = centralNow.toTimeString().slice(0, 5);

  if (!CHAIN_FALLBACK_HOURS[id]) return false;

  const todayHours = CHAIN_FALLBACK_HOURS[id].filter((h) => h.day === currentDay);
  if (!todayHours.length) return false;

  const nowTime = new Date(`1970-01-01T${currentTime}:00`);

  return todayHours.some(({ start, end }) => {
    const s = new Date(`1970-01-01T${start}:00`);
    const e = new Date(`1970-01-01T${end}:00`);
    return nowTime >= s && nowTime <= e;
  });
};

/* ----------------------------------------------------
   5. Updated Time Error Modal (API preferred)
---------------------------------------------------- */
export const showTimeError = (id, hoursFromAPI) => {
  // If API hours exist → show API formatted
  if (hoursFromAPI && hoursFromAPI.time_zone) {
    return showRestaurantTimeErrorAPI(hoursFromAPI);
  }

  // Otherwise fallback hardcoded
  return showTimeErrorFallback(id);
};

/* --- Fallback Modal (hardcoded chains) --- */
const showTimeErrorFallback = (id) => {
  let message = "";

  if (id === 7) {
    message = "Hours: Mon–Sun 11:00AM–9:00PM Central Time, Tuesday CLOSED.";
  } else if (id === 11) {
    message = "Hours: Mon–Thu 11–2:30, 4:30–9:30; Fri–Sat 11–10; Sun 11–9:30.";
  } else if (id === 40) {
    message = "Hours: Mon–Sat 10AM–7PM; Sunday CLOSED.";
  } else if (id === 73) {
    message = "Hours: Mon–Sat 10:30AM–8:30PM; Sunday CLOSED.";
  } else {
    message = "Store hours not available.";
  }

  Swal.fire({
    title: "Please come back later",
    text: message,
    icon: "info",
  });
};

/* --- API Modal --- */
const showRestaurantTimeErrorAPI = (hours) => {
  const timeZone = hours.time_zone || "EST";

  const days = [
    "Monday", "Tuesday", "Wednesday", "Thursday",
    "Friday", "Saturday", "Sunday"
  ];

  const formattedHours = days
    .map((day) => {
      const key = day.toLowerCase();
      const raw = hours[key];
      if (!raw || raw.toLowerCase() === "closed") return `${day}: Closed`;

      const [main, breakStr] = raw.split("|");
      return `${day}: ${main}${breakStr ? ` (Break: ${breakStr})` : ""}`;
    })
    .join("\n");

  let specialSection = "";
  if (hours.specialhours?.length) {
    const specials = hours.specialhours
      .slice(0, 5)
      .map((s) => {
        const date = new Date(s.date).toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
        const status = s.is_closed
          ? "Closed"
          : `${s.open_time}–${s.close_time}`;
        return `${date}: ${status}`;
      })
      .join("<br/>");

    specialSection = `
      <div style="margin-top:10px;text-align:left;">
        <b>Special Hours:</b><br/>
        ${specials}
      </div>`;
  }

  Swal.fire({
    icon: "error",
    title: "Please come back later.",
    html: `
      <div style="text-align:center;">
        <pre style="text-align:left;background:#f5f5f5;padding:10px;border-radius:6px;">
${formattedHours}
        </pre>
        ${specialSection}
        <br/>
        <b>Time Zone:</b> ${timeZone}
      </div>
    `,
    confirmButtonText: "OK",
    confirmButtonColor: "#000",
  });
};



  export const checkIfLunchTime = () => {
    // Get the current date and time in the Eastern Time Zone
    const currentDate = new Date().toLocaleString("en-US", {
        timeZone: "America/New_York", // Set the time zone to Eastern Time Zone
    });

    // Extract the current time in HH:mm format
    const currentTime = new Date(currentDate).toLocaleTimeString("en-US", {
        hour12: false, // Use 24-hour format
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/Chicago", // Central Time Zone for TN
      });

        // Extract the current day
        const currentDay = new Date(currentDate).getDay(); // 0 for Sunday, 1 for Monday, ..., 6 for Saturday

        // Check if it's a weekend (Saturday or Sunday)
        if (currentDay === 0 || currentDay === 6) {
            return false; // Return false if it's a weekend
        }
    

    // Define the store's opening hours
    const openingHours = { start: "12:00", end: "14:30" };

    // Extract hours and minutes from current time
    const [currentHour, currentMinute] = currentTime.split(":").map(Number);

    // Extract hours and minutes from opening hours
    const [startHour, startMinute] = openingHours.start.split(":").map(Number);
    const [endHour, endMinute] = openingHours.end.split(":").map(Number);

    // Check if current time is within lunch hours
    const isLunchTime =
        (currentHour > startHour || (currentHour === startHour && currentMinute >= startMinute)) &&
        (currentHour < endHour || (currentHour === endHour && currentMinute <= endMinute));

    return isLunchTime;
};
